import React, { useState, useEffect } from 'react';
import {
  Modal,
  Paper,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Grid,
  CircularProgress,
  InputAdornment,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  FormHelperText,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import WarningIcon from '@mui/icons-material/Warning';
import ownAuthSession from '../../../services/auth/ownAuthSession';
import { getBankAccountsMap, bankVirman } from '../../../services/migration/financialDataService';

// Para transferi modal penceresi için props arayüzü
interface AccountTransferModalProps {
  open: boolean;
  onClose: () => void;
  companyId: string;
  onSuccess?: () => void;
}

// Banka hesabı arayüzü
interface BankAccount {
  id: string;
  bankName: string;
  branchName: string;
  accountNumber: string;
  iban: string;
  currency: string;
  balance: number;
  subAccounts?: { [key: string]: SubAccount };
}

// Alt hesap arayüzü
interface SubAccount {
  id: string;
  accountName: string;
  accountNumber: string;
  iban: string;
  currency: string;
  balance: number;
  parentId: string;
}

const AccountTransferModal: React.FC<AccountTransferModalProps> = ({
  open,
  onClose,
  companyId,
  onSuccess
}) => {
  // Temel state tanımlamaları
  const [amount, setAmount] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [documentNo, setDocumentNo] = useState<string>('');
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  // Banka ve hesap seçimleri
  const [banks, setBanks] = useState<BankAccount[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  
  // Seçili banka
  const [selectedBank, setSelectedBank] = useState<string>('');
  
  // Seçili banka detayları
  const [bankDetails, setBankDetails] = useState<BankAccount | null>(null);
  
  // Seçili kaynak hesap
  const [sourceAccount, setSourceAccount] = useState<string>('');
  // Seçili hedef hesap
  const [targetAccount, setTargetAccount] = useState<string>('');
  
  // Hesap detayları
  const [sourceAccountDetails, setSourceAccountDetails] = useState<SubAccount | null>(null);
  const [targetAccountDetails, setTargetAccountDetails] = useState<SubAccount | null>(null);

  // Yetersiz bakiye uyarısı modalı için state
  const [insufficientBalanceDialogOpen, setInsufficientBalanceDialogOpen] = useState<boolean>(false);

  // Modal açıldığında verileri yükle
  useEffect(() => {
    if (open && companyId) {
      loadBankAccounts();
    }
  }, [open, companyId]);

  // Modal kapanınca formu sıfırla
  useEffect(() => {
    if (!open) {
      resetForm();
    }
  }, [open]);

  // Formu sıfırlama
  const resetForm = () => {
    setAmount('');
    setDescription('');
    setDocumentNo('');
    setError(null);
    setSuccessMessage(null);
    setSelectedBank('');
    setSourceAccount('');
    setTargetAccount('');
    setBankDetails(null);
    setSourceAccountDetails(null);
    setTargetAccountDetails(null);
  };

  // Bankalar yükleniyor (SQL — gts_accounting_api /api/BankAccount)
  const loadBankAccounts = async () => {
    setLoading(true);
    try {
      const accountsData = await getBankAccountsMap(companyId!);
      
      if (accountsData) {
        const accounts: BankAccount[] = [];
        Object.entries(accountsData).forEach(([accountId, accountData]: [string, any]) => {
          accounts.push({
            id: accountId,
            bankName: accountData.bankName || '',
            branchName: accountData.branchName || '',
            accountNumber: accountData.accountNumber || '',
            iban: accountData.iban || '',
            currency: accountData.currency || 'TRY',
            balance: accountData.balance || 0,
            subAccounts: accountData.hesaplar || undefined
          });
        });
        
        // Alt hesabı olan bankaları filtrele
        const banksWithSubAccounts = accounts.filter(bank => 
          bank.subAccounts && Object.keys(bank.subAccounts).length > 0
        );
        
        // Banka adına göre sırala
        banksWithSubAccounts.sort((a, b) => a.bankName.localeCompare(b.bankName));
        
        setBanks(banksWithSubAccounts);
        
        // İlk bankayı otomatik seç (eğer varsa)
        if (banksWithSubAccounts.length > 0 && !selectedBank) {
          const firstBank = banksWithSubAccounts[0];
          setSelectedBank(firstBank.id);
          setBankDetails(firstBank);
          
          // Alt hesapları varsa ilk iki alt hesabı kaynak ve hedef olarak ayarla
          if (firstBank.subAccounts && Object.keys(firstBank.subAccounts).length >= 2) {
            const subAccountIds = Object.keys(firstBank.subAccounts);
            setSourceAccount(subAccountIds[0]);
            setSourceAccountDetails({
              ...firstBank.subAccounts[subAccountIds[0]],
              id: subAccountIds[0],
              parentId: firstBank.id
            });
            
            setTargetAccount(subAccountIds[1]);
            setTargetAccountDetails({
              ...firstBank.subAccounts[subAccountIds[1]],
              id: subAccountIds[1],
              parentId: firstBank.id
            });
          }
        }
      } else {
        setBanks([]);
      }
    } catch (error) {
      console.error('Banka hesapları yüklenirken hata:', error);
      setError('Banka hesapları yüklenirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  // Banka değiştiğinde
  const handleBankChange = (event: SelectChangeEvent) => {
    const bankId = event.target.value;
    setSelectedBank(bankId);
    
    // Seçili bankayı bul
    const bank = banks.find(b => b.id === bankId);
    setBankDetails(bank || null);
    
    // Seçimleri sıfırla
    setSourceAccount('');
    setTargetAccount('');
    setSourceAccountDetails(null);
    setTargetAccountDetails(null);
    
    // Alt hesapları varsa ilk alt hesabı ve ana hesabı kaynak ve hedef olarak ayarla
    if (bank) {
      // Ana hesaba özel bir ID değeri ("main")
      setSourceAccount("main");
      setSourceAccountDetails({
        id: "main",
        accountName: bank.accountNumber,
        accountNumber: bank.accountNumber,
        iban: bank.iban,
        currency: bank.currency,
        balance: bank.balance,
        parentId: bank.id
      });
      
      // Eğer alt hesaplar varsa, ilk alt hesabı hedef olarak seç
      if (bank.subAccounts && Object.keys(bank.subAccounts).length > 0) {
        const subAccountId = Object.keys(bank.subAccounts)[0];
        setTargetAccount(subAccountId);
        setTargetAccountDetails({
          ...bank.subAccounts[subAccountId],
          id: subAccountId,
          parentId: bank.id
        });
      }
    }
  };

  // Para birimi sembolünü döndür
  const getCurrencySymbol = (currency: string): string => {
    switch(currency) {
      case 'TRY': return '₺';
      case 'USD': return '$';
      case 'EUR': return '€';
      case 'GBP': return '£';
      case 'JPY': return '¥';
      case 'CHF': return 'Fr.';
      case 'AUD': return 'A$';
      case 'CAD': return 'C$';
      case 'CNY': return '¥';
      default: return currency;
    }
  };

  // Kaynak hesap seçildiğinde
  const handleSourceAccountChange = (event: SelectChangeEvent) => {
    const accountId = event.target.value;
    setSourceAccount(accountId);
    
    // Aynı hesabı seçmeyi engelle
    if (accountId === targetAccount) {
      setTargetAccount('');
      setTargetAccountDetails(null);
    }
    
    // Seçilen hesabın detaylarını bul
    if (bankDetails) {
      if (accountId === "main") {
        // Ana hesap seçilmiş
        setSourceAccountDetails({
          id: "main",
          accountName: bankDetails.accountNumber,
          accountNumber: bankDetails.accountNumber,
          iban: bankDetails.iban,
          currency: bankDetails.currency,
          balance: bankDetails.balance,
          parentId: bankDetails.id
        });
      } else if (bankDetails.subAccounts) {
        // Alt hesap seçilmiş
        const subAccount = bankDetails.subAccounts[accountId];
        if (subAccount) {
          setSourceAccountDetails({
            ...subAccount,
            id: accountId,
            parentId: bankDetails.id
          });
        }
      }
    }
  };

  // Hedef hesap seçildiğinde
  const handleTargetAccountChange = (event: SelectChangeEvent) => {
    const accountId = event.target.value;
    setTargetAccount(accountId);
    
    // Aynı hesabı seçmeyi engelle
    if (accountId === sourceAccount) {
      setSourceAccount('');
      setSourceAccountDetails(null);
    }
    
    // Seçilen hesabın detaylarını bul
    if (bankDetails) {
      if (accountId === "main") {
        // Ana hesap seçilmiş
        setTargetAccountDetails({
          id: "main",
          accountName: bankDetails.accountNumber,
          accountNumber: bankDetails.accountNumber,
          iban: bankDetails.iban,
          currency: bankDetails.currency,
          balance: bankDetails.balance,
          parentId: bankDetails.id
        });
      } else if (bankDetails.subAccounts) {
        // Alt hesap seçilmiş
        const subAccount = bankDetails.subAccounts[accountId];
        if (subAccount) {
          setTargetAccountDetails({
            ...subAccount,
            id: accountId,
            parentId: bankDetails.id
          });
        }
      }
    }
  };

  // Yön değiştirme butonu
  const handleSwapAccounts = () => {
    // Hesapları takas et
    const tempSourceAccount = sourceAccount;
    const tempSourceDetails = sourceAccountDetails;
    
    setSourceAccount(targetAccount);
    setSourceAccountDetails(targetAccountDetails);
    
    setTargetAccount(tempSourceAccount);
    setTargetAccountDetails(tempSourceDetails);
  };

  // Sayısal girdi kontrolü
  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Sayısal değer ve nokta dışındaki karakterleri engelle
    const value = e.target.value.replace(/[^0-9.]/g, '');
    
    // Birden fazla nokta olmasını engelle
    if (value.split('.').length > 2) {
      return;
    }
    
    setAmount(value);
  };

  // Transfer işlemi
  const handleTransfer = async () => {
    // Validasyon işlemleri
    if (!amount || parseFloat(amount) <= 0) {
      setError('Lütfen geçerli bir miktar giriniz.');
      return;
    }
    
    if (!selectedBank) {
      setError('Lütfen bir banka seçiniz.');
      return;
    }
    
    if (!sourceAccount) {
      setError('Lütfen bir kaynak hesap seçiniz.');
      return;
    }
    
    if (!targetAccount) {
      setError('Lütfen bir hedef hesap seçiniz.');
      return;
    }
    
    if (sourceAccount === targetAccount) {
      setError('Kaynak ve hedef hesap aynı olamaz.');
      return;
    }
    
    // Para birimi kontrolü
    if (sourceAccountDetails?.currency !== targetAccountDetails?.currency) {
      setError('Para transferi sadece aynı para birimindeki hesaplar arasında yapılabilir.');
      return;
    }
    
    // Transfer miktarını sayısal değere dönüştür
    const transferAmount = parseFloat(parseFloat(amount).toFixed(5));
    
    // Kaynak hesapta yeterli bakiye var mı kontrolü
    if (sourceAccountDetails && transferAmount > sourceAccountDetails.balance) {
      // Yetersiz bakiye uyarı modalını aç
      setInsufficientBalanceDialogOpen(true);
      return;
    }
    
    // İşleme başla ve durumu güncelle
    setSaving(true);
    setError(null);
    setSuccessMessage(null);
    
    try {
      const createdBy = ownAuthSession.getCurrentEmail() || 'unknown';

      if (sourceAccountDetails && targetAccountDetails && bankDetails) {
        // Atomik SQL virman (aynı banka içi) — gts_accounting_api /api/BankAccount/virman
        // "main" => ana hesap (subAccountId null), aksi halde alt hesap id'si.
        await bankVirman({
          sourceBankAccountId: parseInt(selectedBank, 10),
          sourceBankSubAccountId: sourceAccount === 'main' ? null : parseInt(sourceAccount, 10),
          targetBankAccountId: parseInt(selectedBank, 10),
          targetBankSubAccountId: targetAccount === 'main' ? null : parseInt(targetAccount, 10),
          amount: transferAmount,
          description: description.trim() || undefined,
          documentNo: documentNo.trim() || null,
          createdBy,
        });

        const sourceAccountName = sourceAccount === 'main' ? bankDetails.bankName + ' Ana Hesap' : sourceAccountDetails.accountName;
        const targetAccountName = targetAccount === 'main' ? bankDetails.bankName + ' Ana Hesap' : targetAccountDetails.accountName;
        const newSourceBalance = parseFloat((sourceAccountDetails.balance - transferAmount).toFixed(5));
        const newTargetBalance = parseFloat((targetAccountDetails.balance + transferAmount).toFixed(5));

        const currency = sourceAccountDetails.currency || 'TRY';
        const currencySymbol = getCurrencySymbol(currency);
        const successText = `${transferAmount.toLocaleString('tr-TR')} ${currencySymbol} başarıyla "${sourceAccountName}" hesabından "${targetAccountName}" hesabına virman yapıldı.`;
        setSuccessMessage(successText);

        // Yerel bakiye gösterimini güncelle
        setSourceAccountDetails({ ...sourceAccountDetails, balance: newSourceBalance });
        setTargetAccountDetails({ ...targetAccountDetails, balance: newTargetBalance });

        setTimeout(() => {
          if (onSuccess) onSuccess();
          onClose();
        }, 2000);
      }
    } catch (error: any) {
      console.error('Transfer işlemi sırasında hata oluştu:', error);
      const msg = error?.response?.data?.message || error?.message || 'Lütfen tekrar deneyin.';
      setError('Transfer işlemi sırasında bir hata oluştu. ' + msg);
    } finally {
      setSaving(false);
    }
  };

  // Kaynak hesap bilgisi gösterimi
  const renderSourceInfo = () => {
    return sourceAccountDetails ? (
      <Box sx={{ border: '1px solid #e0e0e0', p: 2, borderRadius: 1, backgroundColor: '#f9f9f9' }}>
        <Typography variant="subtitle2" gutterBottom>
          <AccountBalanceIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'text-bottom' }} />
          Kaynak Hesap
        </Typography>
        <Typography variant="body2" gutterBottom sx={{ fontWeight: 'medium' }}>
          {sourceAccountDetails.accountName || sourceAccountDetails.accountNumber}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Mevcut Bakiye: <strong>{sourceAccountDetails.balance.toLocaleString('tr-TR')} {getCurrencySymbol(sourceAccountDetails.currency)}</strong>
        </Typography>
      </Box>
    ) : null;
  };

  // Hedef hesap bilgisi gösterimi
  const renderTargetInfo = () => {
    return targetAccountDetails ? (
      <Box sx={{ border: '1px solid #e0e0e0', p: 2, borderRadius: 1, backgroundColor: '#f9f9f9' }}>
        <Typography variant="subtitle2" gutterBottom>
          <AccountBalanceIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'text-bottom' }} />
          Hedef Hesap
        </Typography>
        <Typography variant="body2" gutterBottom sx={{ fontWeight: 'medium' }}>
          {targetAccountDetails.accountName || targetAccountDetails.accountNumber}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Mevcut Bakiye: <strong>{targetAccountDetails.balance.toLocaleString('tr-TR')} {getCurrencySymbol(targetAccountDetails.currency)}</strong>
        </Typography>
      </Box>
    ) : null;
  };

  // Para birimi uyuşmazlığı kontrolü
  const currencyMismatch = sourceAccountDetails && targetAccountDetails && 
                           sourceAccountDetails.currency !== targetAccountDetails.currency;

  // Banka seçilmiş ve alt hesapları var mı kontrolü
  const hasSubAccounts = bankDetails?.subAccounts && 
                        Object.keys(bankDetails.subAccounts).length > 0;

  return (
    <>
      <Modal 
        open={open} 
        onClose={!saving ? onClose : undefined}
        aria-labelledby="hesap-virman-modal"
      >
        <Paper 
          sx={{ 
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '90%', sm: 600 },
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
            maxHeight: '90vh',
            overflowY: 'auto'
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" component="h2" fontWeight="bold" color="primary">
              Hesaba Virman
            </Typography>
            <IconButton onClick={onClose} size="small" disabled={saving}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Banka Seçimi */}
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel id="bank-selection-label">Banka</InputLabel>
            <Select
              labelId="bank-selection-label"
              value={selectedBank}
              label="Banka"
              onChange={handleBankChange}
            >
              {banks.map(bank => (
                <MenuItem key={bank.id} value={bank.id}>
                  {bank.bankName} - {bank.branchName || 'Ana Şube'}
                </MenuItem>
              ))}
            </Select>
            {banks.length === 0 && (
              <FormHelperText error>Alt hesabı olan banka bulunamadı</FormHelperText>
            )}
          </FormControl>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {!hasSubAccounts && selectedBank && (
                <Alert severity="warning" sx={{ mb: 3 }}>
                  Seçilen bankanın alt hesapları bulunmamaktadır. Transfer yapmak için alt hesaplara sahip bir banka seçiniz.
                </Alert>
              )}

              {hasSubAccounts && (
                <>
                  <Grid container spacing={3}>
                    {/* Kaynak Hesap Seçimi */}
                    <Grid item xs={12} md={5}>
                      <FormControl fullWidth>
                        <InputLabel id="source-account-label">Kaynak Hesap</InputLabel>
                        <Select
                          labelId="source-account-label"
                          value={sourceAccount}
                          label="Kaynak Hesap"
                          onChange={handleSourceAccountChange}
                        >
                          {/* Ana hesap seçeneği */}
                          {bankDetails && (
                            <MenuItem key="main" value="main">
                              {bankDetails.bankName} - Ana Hesap - {bankDetails.accountNumber} ({getCurrencySymbol(bankDetails.currency)})
                            </MenuItem>
                          )}
                          {/* Alt hesap seçenekleri */}
                          {bankDetails && bankDetails.subAccounts && 
                            Object.entries(bankDetails.subAccounts).map(([id, account]) => (
                              <MenuItem key={id} value={id}>
                                {bankDetails.bankName} - {account.accountName} - {account.accountNumber} ({getCurrencySymbol(account.currency)})
                              </MenuItem>
                            ))
                          }
                        </Select>
                      </FormControl>
                    </Grid>

                    {/* Yön Değiştirme Butonu */}
                    <Grid item xs={12} md={2} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                      <IconButton
                        onClick={handleSwapAccounts}
                        color="primary"
                        sx={{
                          border: '1px dashed',
                          p: 1.5,
                          borderRadius: '50%'
                        }}
                      >
                        <SwapHorizIcon />
                      </IconButton>
                    </Grid>

                    {/* Hedef Hesap Seçimi */}
                    <Grid item xs={12} md={5}>
                      <FormControl fullWidth>
                        <InputLabel id="target-account-label">Hedef Hesap</InputLabel>
                        <Select
                          labelId="target-account-label"
                          value={targetAccount}
                          label="Hedef Hesap"
                          onChange={handleTargetAccountChange}
                        >
                          {/* Ana hesap seçeneği (eğer kaynak olarak seçilmemişse) */}
                          {bankDetails && sourceAccount !== "main" && (
                            <MenuItem key="main" value="main">
                              {bankDetails.bankName} - Ana Hesap - {bankDetails.accountNumber} ({getCurrencySymbol(bankDetails.currency)})
                            </MenuItem>
                          )}
                          {/* Alt hesap seçenekleri (kaynak hesap hariç) */}
                          {bankDetails && bankDetails.subAccounts && 
                            Object.entries(bankDetails.subAccounts)
                              .filter(([id]) => id !== sourceAccount) // Kaynak hesabı hariç tut
                              .map(([id, account]) => (
                                <MenuItem key={id} value={id}>
                                  {bankDetails.bankName} - {account.accountName} - {account.accountNumber} ({getCurrencySymbol(account.currency)})
                                </MenuItem>
                              ))
                          }
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>

                  {/* Para Birimi Uyarısı */}
                  {currencyMismatch && (
                    <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
                      Transfer işlemi sadece aynı para birimindeki hesaplar arasında yapılabilir. 
                      Seçilen hesapların para birimleri farklı: 
                      {sourceAccountDetails?.currency} ve {targetAccountDetails?.currency}.
                    </Alert>
                  )}

                  {/* Bilgi Gösterimi */}
                  <Box sx={{ my: 3 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={5}>
                        {renderSourceInfo()}
                      </Grid>
                      <Grid item xs={12} md={2} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <SwapHorizIcon color="primary" />
                      </Grid>
                      <Grid item xs={12} md={5}>
                        {renderTargetInfo()}
                      </Grid>
                    </Grid>
                  </Box>

                  <Divider sx={{ my: 3 }} />

                  {/* Transfer İşlem Bilgileri */}
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        autoFocus
                        required
                        fullWidth
                        label="Transfer Miktarı"
                        value={amount}
                        onChange={handleAmountChange}
                        type="text"
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              {sourceAccountDetails ? getCurrencySymbol(sourceAccountDetails.currency) : ''}
                            </InputAdornment>
                          ),
                        }}
                        error={!!error && !amount}
                        helperText={!amount && !!error ? 'Bu alan zorunludur' : ''}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Belge/Dekont No"
                        value={documentNo}
                        onChange={(e) => setDocumentNo(e.target.value)}
                        placeholder="Varsa belge numarası"
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Açıklama"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        multiline
                        rows={3}
                        placeholder="İsteğe bağlı açıklama ekleyebilirsiniz"
                      />
                    </Grid>
                  </Grid>
                </>
              )}

              {successMessage && (
                <Alert severity="success" sx={{ mt: 2, mb: 1 }}>
                  {successMessage}
                </Alert>
              )}

              {error && (
                <Alert severity="error" sx={{ mt: 2, mb: 1 }}>
                  {error}
                </Alert>
              )}
            </>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3, gap: 2 }}>
            <Button 
              variant="outlined" 
              onClick={onClose}
              disabled={saving}
            >
              İptal
            </Button>
            <Button 
              variant="contained" 
              onClick={handleTransfer}
              disabled={
                !amount || 
                saving || 
                parseFloat(amount) <= 0 || 
                !selectedBank ||
                !sourceAccount || 
                !targetAccount ||
                sourceAccount === targetAccount ||
                !!currencyMismatch ||
                !!(sourceAccountDetails && parseFloat(amount) > sourceAccountDetails.balance)
              }
              startIcon={saving ? <CircularProgress size={24} /> : undefined}
            >
              {saving ? 'İşleniyor...' : 'Transfer Yap'}
            </Button>
          </Box>
        </Paper>
      </Modal>

      {/* Yetersiz Bakiye Uyarı Modalı */}
      <Dialog
        open={insufficientBalanceDialogOpen}
        onClose={() => setInsufficientBalanceDialogOpen(false)}
        aria-labelledby="insufficient-balance-dialog-title"
      >
        <DialogTitle id="insufficient-balance-dialog-title" sx={{ bgcolor: 'error.main', color: 'white' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <WarningIcon sx={{ mr: 1 }} />
            Yetersiz Bakiye
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mt: 2 }}>
            {sourceAccountDetails && (
              <>
                <Typography variant="body1" gutterBottom>
                  <strong>{sourceAccount === "main" ? `${bankDetails?.bankName} Ana Hesap` : sourceAccountDetails.accountName}</strong> 
                  &nbsp;hesabında yeterli bakiye bulunmamaktadır.
                </Typography>
                <Box sx={{ mt: 2, mb: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                  <Typography variant="body2">
                    Transfer Tutarı: <strong>{parseFloat(amount).toLocaleString('tr-TR')} {getCurrencySymbol(sourceAccountDetails.currency)}</strong>
                  </Typography>
                  <Typography variant="body2">
                    Mevcut Bakiye: <strong>{sourceAccountDetails.balance.toLocaleString('tr-TR')} {getCurrencySymbol(sourceAccountDetails.currency)}</strong>
                  </Typography>
                  <Typography variant="body2" color="error.main" sx={{ mt: 1 }}>
                    Eksik Tutar: <strong>{(parseFloat(amount) - sourceAccountDetails.balance).toLocaleString('tr-TR')} {getCurrencySymbol(sourceAccountDetails.currency)}</strong>
                  </Typography>
                </Box>
                <Typography variant="body2">
                  Lütfen farklı bir kaynak hesap seçin veya daha düşük bir tutar girin.
                </Typography>
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setInsufficientBalanceDialogOpen(false)} 
            variant="contained"
            color="primary"
          >
            Tamam
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AccountTransferModal; 